import { useEffect, useState } from "react";
import { Slip } from "./Slip";
import { useTheme } from "../lib/theme";

const NAV = [
  { id: "about", idx: "01", label: "about" },
  { id: "work", idx: "02", label: "work history" },
  { id: "lab", idx: "03", label: "the film lab" },
  { id: "projects", idx: "04", label: "other projects" },
  { id: "letterboxd", idx: "05", label: "letterboxd" },
  { id: "cars", idx: "06", label: "cars i've owned" },
];

export function Sidebar({ active }: { active: string }) {
  const { theme, toggle } = useTheme();
  // Phones only: the menu collapses behind a button (desktop ignores this state).
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <aside className="sticky top-0 z-20 flex w-full flex-col border-b border-line bg-bg-1/95 backdrop-blur md:h-dvh md:w-[288px] md:min-w-[288px] md:border-b-0 md:border-r">
      {/* Top bar: the wordmark, plus the menu button on phones */}
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 md:px-6 md:pb-0 md:pt-7">
        <a href="#about" onClick={close} className="block text-[16px] leading-none text-amber hover:text-amber-hi md:text-[20px]">
          <Slip k="name" />
        </a>
        <button
          type="button"
          className="btn !px-2.5 !py-1 md:!hidden"
          aria-expanded={open}
          aria-controls="site-menu"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="etch-label !text-[11px] !text-inherit">{open ? "✕ close" : "☰ menu"}</span>
        </button>
      </div>

      {/* Menu: always shown as the sidebar on desktop; on phones a panel under the
          top bar, opened by the button and closed by any link or Escape. */}
      <div
        id="site-menu"
        className={[
          "flex-col overflow-y-auto px-5 pb-5 md:flex md:flex-1 md:overflow-visible md:px-6 md:pb-7",
          "max-h-[calc(100dvh-56px)] md:max-h-none",
          open ? "flex border-t border-line pt-3 md:border-t-0 md:pt-0" : "hidden",
        ].join(" ")}
      >
        {/* Nav */}
        <nav className="flex flex-col gap-0.5 md:mt-10">
          {NAV.map((n) => {
            const on = active === n.id;
            return (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={close}
                className={[
                  "flex items-center gap-2.5 rounded px-3 py-2 text-[13px] uppercase tracking-[0.1em] transition-colors md:text-[14px]",
                  "border-l-2 md:border-l-2",
                  on
                    ? "border-amber bg-amber/10 font-semibold text-amber shadow-glow md:shadow-glow"
                    : "border-transparent text-text-dim hover:bg-amber/5 hover:text-text",
                ].join(" ")}
                aria-current={on ? "true" : undefined}
              >
                <span className="font-mono text-[9px] text-text-mute">{n.idx}</span>
                {n.label}
              </a>
            );
          })}
        </nav>

        <div className="hidden flex-1 md:block" />

        {/* Contact */}
        <div className="mt-6 flex flex-col gap-2 md:mt-0">
          <div className="etch-label !text-text-mute">contact</div>
          <a
            href="mailto:aj@grstnhbr.com"
            className="flex items-center gap-2 text-[14px] text-text-dim hover:text-amber"
          >
            <span className="text-amber">✉</span> aj@grstnhbr.com
          </a>
          <a
            href="https://letterboxd.com/_aj/"
            target="_blank"
            rel="noopener"
            className="flex items-center gap-2 text-[14px] text-text-dim hover:text-amber"
          >
            <span className="text-amber">▸</span> letterboxd / @_aj
          </a>
          <a
            href="https://github.com/grstnhbr"
            target="_blank"
            rel="noopener"
            className="flex items-center gap-2 text-[14px] text-text-dim hover:text-amber"
          >
            <span className="text-amber">▸</span> github / grstnhbr
          </a>
          <a
            href="/AJ-Gerstenhaber-Resume.pdf"
            download="AJ Gerstenhaber Resume.pdf"
            className="flex items-center gap-2 text-[14px] text-text-dim hover:text-amber"
          >
            <span className="text-amber">↓</span> download resume
          </a>
        </div>

        <button
          onClick={toggle}
          className="btn mt-5 self-start !px-3 !py-1.5 md:mt-4 md:self-stretch"
          title="Toggle light / dark"
          aria-label="Toggle theme"
        >
          <span className="etch-label !text-[10px] !text-inherit">
            {theme === "dark" ? "◐ light" : "◑ dark"}
          </span>
        </button>
      </div>
    </aside>
  );
}
